import { useState } from 'react';
import { HistoryOutlined } from '@ant-design/icons';

import { Button, Empty, Field, Input, Separator, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { CAC_O_ANH, TEN_TOI_DA, VIET_TAT_TOI_DA } from '../constants';
import {
  useCapNhatHoSo,
  useGoLogo,
  useHoSoToChuc,
  useThayLogo,
} from '../hooks/useHoSoToChuc';
import { formTuHoSo, kiemForm, phanThayDoi } from '../nghiep-vu/ho-so';
import type { FormHoSo } from '../nghiep-vu/ho-so';
import type { LogoSlot, TenantProfile } from '../types';
import { HopLichSu } from './HopLichSu';
import { OAnhHoSo } from './OAnhHoSo';

const URL_THEO_O: Record<LogoSlot, (p: TenantProfile) => string | null> = {
  light: (p) => p.logo_light_url,
  dark: (p) => p.logo_dark_url,
  favicon: (p) => p.favicon_url,
};

/** Khối "Nhận diện tổ chức" của màn Giao diện (ADR-UI-0016, ADR-UI-0018). Màn cha kiểm quyền. */
export function TabToChuc({ onDirty }: { onDirty: (dirty: boolean) => void }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const hoSo = useHoSoToChuc();

  if (hoSo.isPending) {
    return (
      <div className="space-y-4" aria-busy>
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (hoSo.isError) {
    return (
      <Empty
        title={t('hoSoToChuc.loiTai')}
        description={dichLoi(hoSo.error) || t('hoSoToChuc.loiTaiMo')}
        action={
          <Button variant="secondary" size="sm" onClick={() => void hoSo.refetch()}>
            {t('chung.thuLai')}
          </Button>
        }
      />
    );
  }

  return <NoiDung hoSo={hoSo.data} onDirty={onDirty} />;
}

function NoiDung({ hoSo, onDirty }: { hoSo: TenantProfile; onDirty: (dirty: boolean) => void }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const capNhat = useCapNhatHoSo();
  const thayLogo = useThayLogo();
  const goLogo = useGoLogo();

  const [form, setForm] = useState<FormHoSo>(() => formTuHoSo(hoSo));
  const [daThuLuu, setDaThuLuu] = useState(false);
  const [moLichSu, setMoLichSu] = useState(false);

  /*
   * Hồ sơ đổi từ phía server (lưu xong, hoặc tải lại sau `412`) thì form lấy
   * theo. Chỉ so tên, không so phiên bản: đổi logo cũng tăng phiên bản, mà
   * không được xoá chữ người dùng đang gõ dở.
   */
  const [gocDaThay, setGocDaThay] = useState(formTuHoSo(hoSo));
  if (gocDaThay.display_name !== hoSo.display_name || gocDaThay.short_name !== hoSo.short_name) {
    setGocDaThay(formTuHoSo(hoSo));
    setForm(formTuHoSo(hoSo));
    setDaThuLuu(false);
  }

  const loiForm = kiemForm(form);
  const du = phanThayDoi(hoSo, form);
  const coThayDoi = Object.keys(du).length > 0;

  const sua = (patch: Partial<FormHoSo>) => {
    const moi = { ...form, ...patch };
    setForm(moi);
    onDirty(Object.keys(phanThayDoi(hoSo, moi)).length > 0);
  };

  const luu = () => {
    setDaThuLuu(true);
    if (Object.keys(loiForm).length > 0 || !coThayDoi) return;
    capNhat.mutate(
      { v: du, phienBan: hoSo.version },
      {
        onSuccess: () => onDirty(false),
        // `412`: form sắp được thay bằng bản mới nhất, nên không còn gì chưa lưu.
        onError: (err) => err.status === 412 && onDirty(false),
      },
    );
  };

  const boThayDoi = () => {
    setForm(formTuHoSo(hoSo));
    setDaThuLuu(false);
    capNhat.reset();
    onDirty(false);
  };

  const oDangXuLy = (slot: LogoSlot): boolean =>
    (thayLogo.isPending && thayLogo.variables?.v.slot === slot) ||
    (goLogo.isPending && goLogo.variables?.v === slot);

  /** Lỗi server của lần thao tác ảnh gần nhất, gắn vào đúng ô vừa thao tác. */
  const loiO = (slot: LogoSlot): string | undefined => {
    if (thayLogo.isError && thayLogo.variables?.v.slot === slot) return dichLoi(thayLogo.error);
    if (goLogo.isError && goLogo.variables?.v === slot) return dichLoi(goLogo.error);
    return undefined;
  };

  const hienLoi = (khoa?: Parameters<typeof t>[0]) => (daThuLuu && khoa ? t(khoa) : undefined);

  const nguoiSua = hoSo.updated_by;
  const dongPhienBan = nguoiSua
    ? t('hoSoToChuc.phienBanHienTaiBoi', {
        so: hoSo.version,
        luc: ngayGioVN(hoSo.updated_at, locale),
        nguoi: nguoiSua,
      })
    : t('hoSoToChuc.phienBanHienTai', { so: hoSo.version, luc: ngayGioVN(hoSo.updated_at, locale) });

  return (
    <div className="space-y-6">
      <p className="text-xs text-content-muted">{t('hoSoToChuc.moTa')}</p>

      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          luu();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
        <Field
          label={t('hoSoToChuc.tenHienThi')}
          hint={t('hoSoToChuc.tenHienThiGoiY')}
          error={hienLoi(loiForm.display_name)}
          required
        >
          {(p) => (
            <Input
              {...p}
              value={form.display_name}
              maxLength={TEN_TOI_DA + 20}
              onChange={(e) => sua({ display_name: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('hoSoToChuc.tenVietTat')}
          hint={t('hoSoToChuc.tenVietTatGoiY')}
          error={hienLoi(loiForm.short_name)}
          required
        >
          {(p) => (
            <Input
              {...p}
              value={form.short_name}
              maxLength={VIET_TAT_TOI_DA + 8}
              onChange={(e) => sua({ short_name: e.target.value })}
            />
          )}
        </Field>
        </div>

        {capNhat.isError && <LoiTrongHop>{capNhat.error.status === 412 ? t('hoSoToChuc.loi.phienBanCu') : dichLoi(capNhat.error)}</LoiTrongHop>}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="sm" disabled={!coThayDoi || capNhat.isPending} onClick={boThayDoi}>
            {t('hoSoToChuc.boThayDoi')}
          </Button>
          <Button variant="primary" size="sm" htmlType="submit" loading={capNhat.isPending} disabled={!coThayDoi}>
            {capNhat.isPending ? t('hoSoToChuc.dangLuu') : t('hoSoToChuc.luu')}
          </Button>
        </div>
      </form>

      <Separator />

      <div className="space-y-4">
        <p className="text-2xs text-content-muted">{t('hoSoToChuc.anhApDungNgay')}</p>
        <div className="grid gap-5 lg:grid-cols-3">
        {CAC_O_ANH.map((cau) => (
          <OAnhHoSo
            key={cau.slot}
            cau={cau}
            url={URL_THEO_O[cau.slot](hoSo)}
            dangXuLy={oDangXuLy(cau.slot)}
            loiMay={loiO(cau.slot)}
            onChon={(tep) => {
              goLogo.reset();
              thayLogo.mutate({ v: { slot: cau.slot, tep }, phienBan: hoSo.version });
            }}
            onGo={() => {
              thayLogo.reset();
              goLogo.mutate({ v: cau.slot, phienBan: hoSo.version });
            }}
          />
        ))}
        </div>
      </div>

      <Separator />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 text-2xs text-content-muted">{dongPhienBan}</p>
        <Button variant="secondary" size="sm" icon={<HistoryOutlined />} onClick={() => setMoLichSu(true)}>
          {t('hoSoToChuc.lichSuNut')}
        </Button>
      </div>

      <HopLichSu open={moLichSu} phienBanHienTai={hoSo.version} onClose={() => setMoLichSu(false)} />
    </div>
  );
}
